import React, { useState } from "react";
import {
  FiHash,
  FiHome,
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import Statemaster from "./Statemaster.json";
import useApi from "../../../CustomHooks/useApi";
import { checkUID } from "./ValidateAadhar";

const AadhaarDetailsSection = ({ onSave, onEdit }) => {
  /* ---------------- COLLAPSE STATE (UNCHANGED) ---------------- */
  const [isOpen, setIsOpen] = useState(true);

  /* ---------------- FORM STATE ---------------- */
  const [form, setForm] = useState({
    aadhaarNumber: "",
    state: "",
    district: "",
    houseAddress: "",
    landmark: "",
    pincode: "",
  });

  const [aadhaarVerified, setAadhaarVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [errors, setErrors] = useState({});

  /* ---------------- SAVE / RE-ENTER STATES ---------------- */
  const [isSaved, setIsSaved] = useState(false);
  const [isReEnterAadhaar, setIsReEnterAadhaar] = useState(false); // 🔥 NEW

  const { callApi: aadhaarVerify } = useApi(
    "/staff/onboard/verify/adhaar",
    "GET"
  );

  /* ---------------- VERIFY / RE-VERIFY AADHAAR ---------------- */

  const verifyAadhaar = async () => {
    if (!checkUID(form.aadhaarNumber)) {
      setErrors({ aadhaarNumber: "Enter valid 12-digit Aadhaar number" });
      return;
    }

    try {
      setIsVerifying(true);
      setErrors({});

      const res = await aadhaarVerify(
        null,
        `?adhaar=${form.aadhaarNumber}`
      );

      if (res === true) {
        setAadhaarVerified(true);
        setIsReEnterAadhaar(false); // 🔓 show address again
      } else {
        setErrors({ aadhaarNumber: "Aadhaar verification failed" });
      }
    } finally {
      setIsVerifying(false);
    }
  };

  /* ---------------- VALIDATION (ON SAVE ONLY) ---------------- */

  const validate = () => {
    const e = {};
    Object.keys(form).forEach((k) => {
      if (!form[k]) e[k] = "Required";
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ---------------- SAVE ---------------- */

  const handleSave = () => {
    if (!aadhaarVerified) return;
    if (!validate()) return;

    setIsSaved(true);
    onSave(form);
  };

  /* ---------------- EDIT ---------------- */

  const handleEdit = () => {
    setIsSaved(false);
    onEdit();
  };

  /* ---------------- RE-ENTER AADHAAR ---------------- */

  const handleReEnterAadhaar = () => {
    setIsReEnterAadhaar(true);
    setAadhaarVerified(false);
    setErrors({});
    onEdit();

    setForm({
      aadhaarNumber: "",
      state: "",
      district: "",
      houseAddress: "",
      landmark: "",
      pincode: "",
    });
  };

  const disabled = isSaved;

  /* ---------------- UI (UNCHANGED) ---------------- */

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      {/* HEADER */}
      <div
        className="flex cursor-pointer items-center justify-between border-b px-6 py-4"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <h3 className="text-sm font-semibold text-gray-800">
          Aadhaar Information
        </h3>

        {isOpen ? (
          <FiChevronUp className="text-gray-600" />
        ) : (
          <FiChevronDown className="text-gray-600" />
        )}
      </div>

      {isOpen && (
        <div className="p-6">
          {/* AADHAAR NUMBER */}
          <div className="mb-6">
            <label className="text-xs font-medium text-gray-600">
              Aadhaar Number *
            </label>

            <div className="relative mt-1">
              <FiHash className="absolute left-3 top-3 text-gray-400" />
              <input
                disabled={aadhaarVerified && !isReEnterAadhaar}
                className="w-full rounded-md border px-9 py-2 text-sm bg-gray-50"
                value={form.aadhaarNumber}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    aadhaarNumber: e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 12),
                  }))
                }
                placeholder="Enter 12-digit Aadhaar"
              />

              {aadhaarVerified && (
                <FiCheckCircle className="absolute right-3 top-3 text-green-600" />
              )}
            </div>

            {errors.aadhaarNumber && (
              <p className="mt-1 text-xs text-red-500">
                {errors.aadhaarNumber}
              </p>
            )}

            {/* FIRST VERIFY */}
            {!aadhaarVerified && !isReEnterAadhaar && (
              <button
                onClick={verifyAadhaar}
                disabled={isVerifying}
                className="mt-3 rounded-full bg-[#0A2A4F] px-6 py-2 text-sm text-white hover:bg-[#08305e]"
              >
                {isVerifying
                  ? "Verifying Aadhaar..."
                  : "Verify Aadhaar"}
              </button>
            )}

            {/* RE-ENTER AADHAAR */}
            {isSaved && !isReEnterAadhaar && (
              <button
                onClick={handleReEnterAadhaar}
                className="mt-3 ml-3 rounded-full bg-gray-500 px-6 py-2 text-sm text-white"
              >
                Re-Enter Aadhaar
              </button>
            )}

            {/* RE-VERIFY AADHAAR */}
            {isReEnterAadhaar && (
              <button
                onClick={verifyAadhaar}
                disabled={isVerifying}
                className="mt-3 rounded-full bg-[#0A2A4F] px-6 py-2 text-sm text-white hover:bg-[#08305e]"
              >
                {isVerifying
                  ? "Re-Verifying Aadhaar..."
                  : "Re-Verify Aadhaar"}
              </button>
            )}
          </div>

          {/* ADDRESS DETAILS */}
          {aadhaarVerified && !isReEnterAadhaar && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* State */}
                <div>
                  <label className="text-xs font-medium text-gray-600">
                    State *
                  </label>
                  <select
                    disabled={disabled}
                    className="w-full rounded-md border px-3 py-2 text-sm"
                    value={form.state}
                    onChange={(e) =>
                      setForm({ ...form, state: e.target.value })
                    }
                  >
                    <option value="">Select State</option>
                    {Statemaster.states.map((s) => (
                      <option key={s.state} value={s.state}>
                        {s.state}
                      </option>
                    ))}
                  </select>
                  {errors.state && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.state}
                    </p>
                  )}
                </div>

                {/* District */}
                <div>
                  <label className="text-xs font-medium text-gray-600">
                    District *
                  </label>
                  <select
                    disabled={disabled}
                    className="w-full rounded-md border px-3 py-2 text-sm"
                    value={form.district}
                    onChange={(e) =>
                      setForm({ ...form, district: e.target.value })
                    }
                  >
                    <option value="">Select District</option>
                    {Statemaster.states
                      .find((s) => s.state === form.state)
                      ?.districts.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                  </select>
                  {errors.district && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.district}
                    </p>
                  )}
                </div>

                {/* House Address */}
                <div>
                  <label className="text-xs font-medium text-gray-600">
                    House Address *
                  </label>
                  <div className="relative">
                    <FiHome className="absolute left-3 top-3 text-gray-400" />
                    <input
                      disabled={disabled}
                      className="w-full rounded-md border px-9 py-2 text-sm"
                      value={form.houseAddress}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          houseAddress: e.target.value,
                        })
                      }
                    />
                  </div>
                  {errors.houseAddress && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.houseAddress}
                    </p>
                  )}
                </div>

                {/* Landmark */}
                <div>
                  <label className="text-xs font-medium text-gray-600">
                    Landmark *
                  </label>
                  <input
                    disabled={disabled}
                    className="w-full rounded-md border px-3 py-2 text-sm"
                    value={form.landmark}
                    onChange={(e) =>
                      setForm({ ...form, landmark: e.target.value })
                    }
                  />
                  {errors.landmark && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.landmark}
                    </p>
                  )}
                </div>

                {/* Pincode */}
                <div>
                  <label className="text-xs font-medium text-gray-600">
                    Pincode *
                  </label>
                  <input
                    disabled={disabled}
                    className="w-full rounded-md border px-3 py-2 text-sm"
                    value={form.pincode}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        pincode: e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 6),
                      }))
                    }
                  />
                  {errors.pincode && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.pincode}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-6">
                {!isSaved ? (
                  <button
                    onClick={handleSave}
                    className="rounded-full bg-green-600 px-8 py-2 text-sm text-white"
                  >
                    Save
                  </button>
                ) : (
                  <button
                    onClick={handleEdit}
                    className="rounded-full bg-gray-500 px-8 py-2 text-sm text-white"
                  >
                    Edit
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AadhaarDetailsSection;

